import { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSearchAvailableAreaCodes } from '@forwardflow/queries/onboarding.queries'
import { Screen } from '../frame/SetupFrame'
import { useStepFooter } from '../../hooks/useStepFooter'
import { useAreaCodeSuggestions } from '../../hooks/useAreaCodeSuggestions'
import { useDebounced } from '../../hooks/useDebounced'
import { LinkButton, RowCard, SearchPick, Skeleton } from '../ui/primitives'
import { Icon } from '../ui/Icon'
import { MAX_AREA_CODES } from '../../utils/constants'

/**
 * Step 7: the area code leads see.
 *
 * Suggested from the team's own mobile numbers (the owner's first), each
 * checked against the number inventory, the first available one
 * pre-selected. Any other area code or city can be searched. The numbers
 * themselves are bought when the first campaign starts.
 */
export const AreaStep = () => {
   const { onboardingData, getNextStep, setCampaignConfiguration } =
      useOnboardingStoreV2()
   const codes = onboardingData.campaignConfiguration?.area_codes || []
   const { suggestions, isSuggesting, ready, ownerCode } =
      useAreaCodeSuggestions()
   const [adding, setAdding] = useState(false)
   const [query, setQuery] = useState('')
   const term = useDebounced(query.trim(), 350)
   const searchable =
      /^\d{3}$/.test(term) || (!/^\d+$/.test(term) && term.length >= 3)
   const { data: found, isLoading: searching } = useSearchAvailableAreaCodes(
      searchable ? term : ''
   )

   // Pre-select the first available suggestion once, if nothing is chosen yet.
   const preselected = useRef(false)
   useEffect(() => {
      if (preselected.current || isSuggesting || codes.length || !ready.length)
         return
      preselected.current = true
      setCampaignConfiguration({ area_codes: [ready[0]] })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuggesting, ready.join(','), codes.length])

   const set = (next: string[]) =>
      setCampaignConfiguration({ area_codes: next })
   const toggle = (code: string) => {
      if (codes.includes(code)) set(codes.filter((c) => c !== code))
      else if (codes.length < MAX_AREA_CODES) set([...codes, code])
   }

   const results = ((found?.areaCodes as unknown[]) || [])
      .map(String)
      .filter((c) => !codes.includes(c))
   const options = results.map((c) => ({
      id: c,
      label: `(${c})`,
      sub: /^\d{3}$/.test(term) ? 'numbers available' : `near ${term}`,
   }))
   const full = codes.length >= MAX_AREA_CODES

   useStepFooter(
      codes.length
         ? `Use ${codes.map((c) => `(${c})`).join(' and ')}`
         : 'Pick an area code to continue',
      () => getNextStep(),
      {
         disabled: !codes.length,
         hint: 'Numbers are bought when your first campaign starts. The first three are included.',
         testId: 'confirm-area',
      }
   )

   const extra = codes.filter((c) => !suggestions.some((s) => s.code === c))

   return (
      <Screen
         stepKey="area-code"
         kind="area"
         title="Your leads will see calls from a local number"
         lede={
            ownerCode
               ? `Your mobile is a (${ownerCode}) number, so we suggest (${ownerCode}). Leads pick up local numbers far more often. Pick up to three.`
               : 'Leads pick up local numbers far more often. Pick up to three area codes, by area code or by city.'
         }
         width="sm"
      >
         {isSuggesting &&
         !suggestions.some((s) => s.available !== undefined) ? (
            <Skeleton className="h-[130px] rounded-[20px]" />
         ) : suggestions.length || extra.length ? (
            <RowCard testId="area-card">
               {suggestions.map((s) => {
                  const on = codes.includes(s.code)
                  const unavailable = s.available === false
                  return (
                     <button
                        key={s.code}
                        type="button"
                        onClick={() => toggle(s.code)}
                        disabled={unavailable || s.isChecking || (!on && full)}
                        data-testid={`code-${s.code}`}
                        className={cn(
                           'w-full flex items-center gap-4 px-5 min-h-[64px] text-left transition-colors disabled:cursor-not-allowed',
                           on
                              ? 'bg-[var(--ob-primary-tint)]'
                              : 'hover:bg-[var(--ob-surface-2)]'
                        )}
                     >
                        <span
                           className={cn(
                              'flex shrink-0 items-center justify-center w-10 h-10 rounded-full',
                              on
                                 ? 'bg-[var(--ob-primary)] text-white'
                                 : 'bg-[var(--ob-surface-2)] text-white'
                           )}
                        >
                           <Icon name={on ? 'check' : 'pin'} size={16} />
                        </span>
                        <span className="flex-1 min-w-0">
                           <span className="block text-[15px] font-semibold">
                              ({s.code})
                           </span>
                           <span className="block text-[13px] text-[var(--ob-text-2)]">
                              {s.isOwner
                                 ? 'Your mobile number'
                                 : `${s.name}’s number`}
                              {s.isChecking
                                 ? ' · checking'
                                 : unavailable
                                   ? ' · no numbers available right now'
                                   : ''}
                           </span>
                        </span>
                        <span
                           className={cn(
                              'text-[13px] font-semibold',
                              on
                                 ? 'text-[var(--ob-light)]'
                                 : unavailable
                                   ? 'text-[var(--ob-text-3)]'
                                   : 'text-[var(--ob-text-2)]'
                           )}
                        >
                           {on
                              ? 'Selected'
                              : unavailable
                                ? 'Unavailable'
                                : 'Add'}
                        </span>
                     </button>
                  )
               })}
               {extra.map((c) => (
                  <div
                     key={c}
                     className="flex items-center gap-4 px-5 min-h-[64px] bg-[var(--ob-primary-tint)]"
                     data-testid={`code-${c}`}
                  >
                     <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[var(--ob-primary)] text-white">
                        <Icon name="check" size={16} />
                     </span>
                     <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-semibold">
                           ({c})
                        </span>
                        <span className="block text-[13px] text-[var(--ob-text-2)]">
                           Added by you
                        </span>
                     </span>
                     <button
                        type="button"
                        onClick={() => toggle(c)}
                        className="text-[13px] font-semibold text-[var(--ob-text-2)] hover:text-white"
                        data-testid={`remove-code-${c}`}
                     >
                        Remove
                     </button>
                  </div>
               ))}
            </RowCard>
         ) : (
            <p
               className="text-center text-[14px] text-[var(--ob-text-2)]"
               data-testid="area-empty"
            >
               No phone numbers on your Follow Up Boss team yet. Search for an
               area code or a city below.
            </p>
         )}
         <div className="mt-4" data-testid="area-editor">
            {adding ? (
               <SearchPick
                  items={options}
                  onQuery={setQuery}
                  loading={searching}
                  placeholder="Search by area code or city, e.g. 616 or Denver"
                  emptyLabel={
                     !searchable
                        ? 'Type three digits, or a city name'
                        : full
                          ? 'Remove an area code to add another'
                          : 'No numbers available there right now. Try a nearby one.'
                  }
                  onPick={(code) => {
                     toggle(code)
                     setQuery('')
                     setAdding(false)
                  }}
                  testId="area-search"
               />
            ) : (
               <div className="text-center">
                  <LinkButton
                     onClick={() => setAdding(true)}
                     testId="change-area"
                  >
                     Search another area code or city
                  </LinkButton>
               </div>
            )}
         </div>
      </Screen>
   )
}
